import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, TextField, TextView } from '@atoms';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  Grid,
  InputLabel,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { RouteComponentProps, Link as RouterLink } from '@reach/router';
import { routes } from '@particles';
import { callGetMarkupValues, callUpdateMarupValues, callCreateMarkup, callDeleteMarkup, callGetAiModels, resolveFallbackMarkupType, useMarkupHistory } from '@api';
import type { AiModel, CreateMarkupPayload, MarkupHistoryRole } from '@api';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow as MuiTableRow,
  Typography
} from '@mui/material';
import { Link } from '@mui/material';


const PURPOSES = ['response', 'sentiment', 'enquiries'] as const;
type Purpose = typeof PURPOSES[number];
const PURPOSE_LABELS: Record<Purpose, string> = {
  response: 'Other response',
  sentiment: 'Sentiment',
  enquiries: 'Enquiries'
};
type PurposeFieldKey =
  | 'responseModel' | 'responseMuValue' | 'responseInputPrice' | 'responseOutputPrice'
  | 'sentimentModel' | 'sentimentMuValue' | 'sentimentInputPrice' | 'sentimentOutputPrice'
  | 'enquiriesModel' | 'enquiriesMuValue' | 'enquiriesInputPrice' | 'enquiriesOutputPrice'
  | 'responseMuValueSales' | 'responseMuValueLettings'
  | 'responseModelSales' | 'responseModelLettings';
const PURPOSE_KEYS: Record<Purpose, { model: PurposeFieldKey; muvalue: PurposeFieldKey; inputPrice: PurposeFieldKey; outputPrice: PurposeFieldKey }> = {
  response:  { model: 'responseModel',  muvalue: 'responseMuValue',  inputPrice: 'responseInputPrice',  outputPrice: 'responseOutputPrice' },
  sentiment: { model: 'sentimentModel', muvalue: 'sentimentMuValue', inputPrice: 'sentimentInputPrice', outputPrice: 'sentimentOutputPrice' },
  enquiries: { model: 'enquiriesModel', muvalue: 'enquiriesMuValue', inputPrice: 'enquiriesInputPrice', outputPrice: 'enquiriesOutputPrice' }
};
// Only the markups are edited per tier. Input/output prices belong to the
// model (AI model registry, edited on the AI Models page) and are shown
// read-only here — the *InputPrice/*OutputPrice tier columns are no longer
// read by billing and are never sent.
const NUMERIC_PURPOSE_KEYS: PurposeFieldKey[] = ['responseMuValue', 'sentimentMuValue', 'enquiriesMuValue'];
const MODEL_PURPOSE_KEYS: PurposeFieldKey[] = ['responseModel', 'sentimentModel', 'enquiriesModel'];
// SR-H6: response-only overrides on responseMuValue. Unlike NUMERIC_PURPOSE_KEYS,
// blank is a meaningful, valid state here ("no override — use responseMuValue"),
// distinct from 0 — so these get their own dirty/save handling that can send an
// explicit `null` to clear a previously-set override, rather than being silently
// skipped the way a blanked mandatory numeric field is.
const OPTIONAL_OVERRIDE_KEYS: PurposeFieldKey[] = ['responseMuValueSales', 'responseMuValueLettings'];
// SR-H6: per-enquiry-type response model. Same blank-means-inherit rule as
// OPTIONAL_OVERRIDE_KEYS, but string-valued: '' in the draft ↔ null on the server.
const OPTIONAL_MODEL_OVERRIDE_KEYS: PurposeFieldKey[] = ['responseModelSales', 'responseModelLettings'];
// The Sales / Lettings rows under Other response: their own model and markup,
// each blank meaning "same as Other response". Prices follow the model.
const ENQUIRY_OVERRIDE_ROWS = [
  { label: 'Sales', model: 'responseModelSales', muvalue: 'responseMuValueSales' },
  { label: 'Lettings', model: 'responseModelLettings', muvalue: 'responseMuValueLettings' }
] as const;

type MarkupField = {
  value: number;
  price?: number;
  title?: string;
  description?: string;
  responseModel?: string;
  responseMuValue?: number;
  responseInputPrice?: number;
  responseOutputPrice?: number;
  sentimentModel?: string;
  sentimentMuValue?: number;
  sentimentInputPrice?: number;
  sentimentOutputPrice?: number;
  enquiriesModel?: string;
  enquiriesMuValue?: number;
  enquiriesInputPrice?: number;
  enquiriesOutputPrice?: number;
  responseMuValueSales?: number | null;
  responseMuValueLettings?: number | null;
  responseModelSales?: string | null;
  responseModelLettings?: string | null;
};
type MarkupFieldDraft = {
  value: string;
  price?: string;
  title?: string;
  description?: string;
  responseModel?: string;
  responseMuValue?: string;
  responseInputPrice?: string;
  responseOutputPrice?: string;
  sentimentModel?: string;
  sentimentMuValue?: string;
  sentimentInputPrice?: string;
  sentimentOutputPrice?: string;
  enquiriesModel?: string;
  enquiriesMuValue?: string;
  enquiriesInputPrice?: string;
  enquiriesOutputPrice?: string;
  // Blank string means "no override" — distinct from '0'.
  responseMuValueSales?: string;
  responseMuValueLettings?: string;
  responseModelSales?: string;
  responseModelLettings?: string;
};
type MarkupFormData = Record<string, MarkupField>;
type MarkupDraftData = Record<string, MarkupFieldDraft>;

const isModelType = (type: string): boolean =>
  type.startsWith('ai_processing');

type RowConfig = {
  type: string;
  label: string;
  hasPrice?: boolean;
  helper?: string;
};

type SectionConfig = {
  title: string;
  description?: string;
  rows: RowConfig[];
  isDynamic?: boolean;
  // Rows are not declared here — the single fallback row is resolved from the
  // `ai_fallback_markup_type` lookup and injected in `loadMarkupValues`.
  isFallback?: boolean;
};

type ModelOptionGroup = { group: string; models: string[] };

// Transform AI model registry rows into the grouped shape the dropdowns expect,
// preserving group order by first-seen sortOrder. Inactive models are dropped
// from the choices (a tier already on one still shows it as its value).
const buildModelOptions = (rows: AiModel[]): ModelOptionGroup[] => {
  const active = rows
    .filter((r) => r.isActive !== false && r.model)
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
  const groups: ModelOptionGroup[] = [];
  const indexByGroup = new Map<string, number>();
  for (const r of active) {
    const group = r.groupName || 'Other';
    const model = r.model;
    if (!indexByGroup.has(group)) {
      indexByGroup.set(group, groups.length);
      groups.push({ group, models: [] });
    }
    groups[indexByGroup.get(group) as number].models.push(model);
  }
  return groups;
};

const BASE_AI_ROWS: RowConfig[] = [
  { type: 'ai_processing', label: 'AI Processing Response', hasPrice: true },
  { type: 'ai_processing_legacy', label: 'AI Processing SR-L (Legacy)', hasPrice: true },
  { type: 'ai_processing_preferred', label: 'AI Processing SR-P (Preferred)', hasPrice: true },
  { type: 'ai_processing_broker', label: 'AI Processing SR-B (Broker)', hasPrice: true },
  { type: 'ai_processing_ran', label: 'AI Processing SR-R (RAN)', hasPrice: true },
  { type: 'ai_processing_claude', label: 'AI Processing SR-C (Claude)', hasPrice: true }
];

const BASE_SECTIONS: SectionConfig[] = [
  {
    title: 'AI Processing',
    description: 'Markup and optional per-unit pricing for AI processing features.',
    rows: BASE_AI_ROWS,
    isDynamic: true
  },
  {
    title: 'AI Processing Fallback',
    description:
      "Used automatically when an AI provider call fails on the account's own tier. Not assignable to accounts.",
    rows: [],
    isFallback: true
  },
  {
    title: 'WABA',
    description: 'WhatsApp template + inbound/outbound message handling.',
    rows: [
      { type: 'whatsapp', label: 'WhatsApp Template' },
      { type: 'whatsapp_inbound', label: 'WhatsApp Inbound', hasPrice: true },
      { type: 'whatsapp_outbound', label: 'WhatsApp Outbound', hasPrice: true }
    ]
  },
  {
    title: 'Twilio',
    description: 'Phone and SMS margins.',
    rows: [
      { type: 'inbound_call', label: 'Inbound Call' },
      { type: 'outbound_call', label: 'Outbound Call' },
      { type: 'inbound_sms', label: 'Inbound SMS' },
      { type: 'outbound_sms', label: 'Outbound SMS' },
      { type: 'twilio_other', label: 'Twilio Other' }
    ]
  },
  {
    title: 'Negotiator',
    rows: [{ type: 'reapit', label: 'Negotiator' }]
  }
];

const BASE_DYNAMIC_TYPES = new Set([
  ...BASE_AI_ROWS.map((r) => r.type)
]);

const slugify = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const buildInitialServerData = (sections: SectionConfig[]): MarkupFormData => {
  const initial: MarkupFormData = {};
  sections.forEach((section) => {
    section.rows.forEach((row) => {
      initial[row.type] = row.hasPrice
        ? { value: 0, price: 0 }
        : { value: 0 };
    });
  });
  return initial;
};

const buildDraftFromServer = (server: MarkupFormData): MarkupDraftData => {
  const draft: MarkupDraftData = {};
  Object.keys(server).forEach((type) => {
    const row = server[type];
    const base: MarkupFieldDraft = {
      value: String(row.value ?? 0),
      ...(row.price !== undefined ? { price: String(row.price ?? 0) } : {}),
      title: row.title ?? '',
      description: row.description ?? ''
    };
    if (isModelType(type)) {
      for (const key of MODEL_PURPOSE_KEYS) {
        base[key] = (row[key] as string | undefined) ?? '';
      }
      for (const key of NUMERIC_PURPOSE_KEYS) {
        const v = row[key] as number | undefined;
        base[key] = String(v ?? 0);
      }
      for (const key of OPTIONAL_OVERRIDE_KEYS) {
        const v = row[key] as number | null | undefined;
        base[key] = v === null || v === undefined ? '' : String(v);
      }
      for (const key of OPTIONAL_MODEL_OVERRIDE_KEYS) {
        base[key] = (row[key] as string | null | undefined) ?? '';
      }
    }
    draft[type] = base;
  });
  return draft;
};

const parseNumber = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const SuperAdminMarkupMargins: React.FC<RouteComponentProps> = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState<SectionConfig[]>(BASE_SECTIONS);
  const [serverData, setServerData] = useState<MarkupFormData>(() => buildInitialServerData(BASE_SECTIONS));
  const [draft, setDraft] = useState<MarkupDraftData>(() => buildDraftFromServer(buildInitialServerData(BASE_SECTIONS)));
  // Per-row audit info (who + when last touched). Kept out of `serverData` /
  // `draft` so save flows and unsaved-change detection don't have to know
  // about it; it's a pure UI concern.
  const [updatedMeta, setUpdatedMeta] = useState<
    Record<string, { updatedAt: string; updatedByUserName: string | null }>
  >({});
  // Field-level change log viewer — populated when the user clicks
  // "View history" next to any row's "Last updated" hint.
  const [historyType, setHistoryType] = useState<string | undefined>();
  const [historyRoleFilter, setHistoryRoleFilter] = useState<'all' | MarkupHistoryRole>('all');
  const historyQuery = useMarkupHistory({
    type: historyType,
    enabled: Boolean(historyType)
  });
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>(
    { open: false, severity: 'success', message: '' }
  );


  // AI model registry (AiModels): the dropdown choices, and the price of each
  // model — shown read-only on every tier row that runs it.
  const [aiModels, setAiModels] = useState<AiModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const aiModelOptions = useMemo(() => buildModelOptions(aiModels), [aiModels]);
  const priceByModel = useMemo(() => new Map(aiModels.map((m) => [m.model, m])), [aiModels]);

  // New markup dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  // Live billing accounts per tier, and which tier is the fallback — together they
  // decide whether a plan can be deleted. The server enforces the same rules; this
  // only decides whether to offer the button.
  const [accountCounts, setAccountCounts] = useState<Record<string, number>>({});
  const [fallbackMarkupType, setFallbackMarkupType] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [dialogPrefix, setDialogPrefix] = useState('ai_processing');
  const [dialogSectionTitle, setDialogSectionTitle] = useState('AI Processing');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  // Legacy single-model fields (used for non-AI sections)
  const [newModel, setNewModel] = useState('');
  const [newMuvalue, setNewMuvalue] = useState('');
  const [newPrice, setNewPrice] = useState('');
  // Per-purpose fields (used when dialogPrefix === 'ai_processing')
  type NewPurposeDraft = { model: string; muvalue: string };
  const emptyPurposeDraft = (): NewPurposeDraft => ({ model: '', muvalue: '' });
  const [newPurposeDrafts, setNewPurposeDrafts] = useState<Record<Purpose, NewPurposeDraft>>(() => ({
    response: emptyPurposeDraft(),
    sentiment: emptyPurposeDraft(),
    enquiries: emptyPurposeDraft()
  }));
  const updatePurposeDraft = useCallback(
    (purpose: Purpose, key: keyof NewPurposeDraft, value: string) => {
      setNewPurposeDrafts((prev) => ({ ...prev, [purpose]: { ...prev[purpose], [key]: value } }));
    },
    []
  );
  // SR-H6: response-only, so kept separate from newPurposeDrafts rather than
  // adding unused fields to the sentiment/enquiries drafts. Blank = no override.
  const [newResponseOverrides, setNewResponseOverrides] = useState({ sales: '', lettings: '' });
  const [creating, setCreating] = useState(false);

  const closeSnack = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);

  const validationErrors = useMemo(() => {
    const errors: Record<string, { value?: string; price?: string } & Partial<Record<PurposeFieldKey, string>>> = {};
    Object.keys(draft).forEach((type) => {
      const v = parseNumber(draft[type].value);
      if (v === null) {
        errors[type] = { ...(errors[type] ?? {}), value: 'Enter a valid number' };
      }
      if (draft[type].price !== undefined) {
        const p = parseNumber(draft[type].price ?? '');
        if (p === null) {
          errors[type] = { ...(errors[type] ?? {}), price: 'Enter a valid number' };
        }
      }
      for (const key of NUMERIC_PURPOSE_KEYS) {
        const raw = draft[type][key];
        if (raw === undefined) continue;
        if (String(raw).trim() === '') continue;
        const n = parseNumber(raw);
        if (n === null) {
          errors[type] = { ...(errors[type] ?? {}), [key]: 'Invalid' };
        }
      }
      // Blank is always valid here ("no override") — only a non-blank,
      // non-numeric value is an error.
      for (const key of OPTIONAL_OVERRIDE_KEYS) {
        const raw = draft[type][key];
        if (raw === undefined) continue;
        if (String(raw).trim() === '') continue;
        const n = parseNumber(raw);
        if (n === null) {
          errors[type] = { ...(errors[type] ?? {}), [key]: 'Invalid' };
        }
      }
    });
    return errors;
  }, [draft]);

  const hasValidationErrors = useMemo(() => Object.keys(validationErrors).length > 0, [validationErrors]);

  const dirtyCount = useMemo(() => {
    let count = 0;
    Object.keys(draft).forEach((type) => {
      const v = parseNumber(draft[type].value);
      if (v !== null && v !== serverData[type]?.value) count += 1;
      if (draft[type].price !== undefined) {
        const p = parseNumber(draft[type].price ?? '');
        if (p !== null && p !== serverData[type]?.price) count += 1;
      }
      if (draft[type].title !== undefined) {
        const nextTitle = (draft[type].title ?? '').trim();
        const serverTitle = (serverData[type]?.title ?? '').trim();
        if (nextTitle && nextTitle !== serverTitle) count += 1;
      }
      if (draft[type].description !== undefined) {
        const nextDesc = draft[type].description ?? '';
        const serverDesc = serverData[type]?.description ?? '';
        if (nextDesc !== serverDesc) count += 1;
      }
      for (const key of MODEL_PURPOSE_KEYS) {
        if (draft[type][key] !== undefined) {
          const next = draft[type][key] ?? '';
          const prev = (serverData[type]?.[key] as string | undefined) ?? '';
          if (next !== prev) count += 1;
        }
      }
      for (const key of NUMERIC_PURPOSE_KEYS) {
        if (draft[type][key] !== undefined) {
          const next = parseNumber(draft[type][key] ?? '');
          const prev = (serverData[type]?.[key] as number | undefined) ?? 0;
          if (next !== null && next !== prev) count += 1;
        }
      }
      for (const key of OPTIONAL_MODEL_OVERRIDE_KEYS) {
        if (draft[type][key] !== undefined) {
          const next = (draft[type][key] ?? '').trim();
          const prev = (serverData[type]?.[key] as string | null | undefined) ?? '';
          if (next !== prev) count += 1;
        }
      }
      for (const key of OPTIONAL_OVERRIDE_KEYS) {
        if (draft[type][key] !== undefined) {
          const raw = draft[type][key] ?? '';
          const prev = (serverData[type]?.[key] as number | null | undefined) ?? null;
          if (raw.trim() === '') {
            if (prev !== null) count += 1; // clearing a previously-set override
          } else {
            const next = parseNumber(raw);
            if (next !== null && next !== prev) count += 1;
          }
        }
      }
    });
    return count;
  }, [draft, serverData]);

  const sectionMeta = useMemo(() => {
    return sections.map((section) => {
      let sectionDirty = 0;
      let sectionErrors = 0;

      section.rows.forEach((row) => {
        const type = row.type;

        const v = parseNumber(draft[type]?.value ?? '');
        if (v !== null && v !== serverData[type]?.value) sectionDirty += 1;

        if (draft[type]?.price !== undefined) {
          const p = parseNumber(draft[type]?.price ?? '');
          if (p !== null && p !== serverData[type]?.price) sectionDirty += 1;
        }

        if (draft[type]?.title !== undefined) {
          const nextTitle = (draft[type].title ?? '').trim();
          const serverTitle = (serverData[type]?.title ?? '').trim();
          if (nextTitle && nextTitle !== serverTitle) sectionDirty += 1;
        }

        if (draft[type]?.description !== undefined) {
          const nextDesc = draft[type].description ?? '';
          const serverDesc = serverData[type]?.description ?? '';
          if (nextDesc !== serverDesc) sectionDirty += 1;
        }

        for (const key of MODEL_PURPOSE_KEYS) {
          if (draft[type]?.[key] !== undefined) {
            const next = draft[type][key] ?? '';
            const prev = (serverData[type]?.[key] as string | undefined) ?? '';
            if (next !== prev) sectionDirty += 1;
          }
        }
        for (const key of NUMERIC_PURPOSE_KEYS) {
          if (draft[type]?.[key] !== undefined) {
            const next = parseNumber(draft[type][key] ?? '');
            const prev = (serverData[type]?.[key] as number | undefined) ?? 0;
            if (next !== null && next !== prev) sectionDirty += 1;
          }
        }
        for (const key of OPTIONAL_MODEL_OVERRIDE_KEYS) {
          if (draft[type]?.[key] !== undefined) {
            const next = (draft[type][key] ?? '').trim();
            const prev = (serverData[type]?.[key] as string | null | undefined) ?? '';
            if (next !== prev) sectionDirty += 1;
          }
        }
        for (const key of OPTIONAL_OVERRIDE_KEYS) {
          if (draft[type]?.[key] !== undefined) {
            const raw = draft[type][key] ?? '';
            const prev = (serverData[type]?.[key] as number | null | undefined) ?? null;
            if (raw.trim() === '') {
              if (prev !== null) sectionDirty += 1;
            } else {
              const next = parseNumber(raw);
              if (next !== null && next !== prev) sectionDirty += 1;
            }
          }
        }

        if (validationErrors[type]?.value) sectionErrors += 1;
        if (validationErrors[type]?.price) sectionErrors += 1;
      });

      return { title: section.title, dirty: sectionDirty, errors: sectionErrors };
    });
  }, [draft, serverData, validationErrors, sections]);

  const onDraftChange = useCallback((type: string, field: 'value' | 'price' | 'title' | 'description' | PurposeFieldKey, raw: string) => {
    setDraft((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: raw
      }
    }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(buildDraftFromServer(serverData));
  }, [serverData]);

  const loadMarkupValues = useCallback(async () => {
    setLoading(true);
    try {
      // Resolve which tier is the fallback before splitting rows into sections, so
      // the fallback never flickers into the assignable "AI Processing" list first.
      const fallbackType = await resolveFallbackMarkupType();
      setFallbackMarkupType(fallbackType);

      const { data, code } = await callGetMarkupValues();
      if (code === 200 && data?.data) {
        // Discover dynamic rows from server data per section prefix
        const dynamicRowsByPrefix: Record<string, RowConfig[]> = {
          ai_processing: []
        };
        // The fallback tier is pulled out of the AI Processing list entirely and
        // rendered in its own section — it is configuration, not a sellable plan.
        const fallbackItem = data.data.find((item: MarkUp) => item.type === fallbackType);
        const fallbackRow: RowConfig | null = fallbackItem
          ? { type: fallbackItem.type, label: fallbackItem.title, hasPrice: true }
          : null;
        data.data.forEach((item: MarkUp) => {
          if (item.type === fallbackType) return;
          if (BASE_DYNAMIC_TYPES.has(item.type)) return;
          for (const prefix of Object.keys(dynamicRowsByPrefix)) {
            if (item.type.startsWith(`${prefix}_`)) {
              dynamicRowsByPrefix[prefix].push({
                type: item.type,
                label: item.title,
                hasPrice: true
              });
              break;
            }
          }
        });

        // Map section title to its prefix for matching
        const sectionPrefixMap: Record<string, string> = {
          'AI Processing': 'ai_processing'
        };

        // Build merged sections
        const mergedSections = BASE_SECTIONS.map((section) => {
          if (section.isFallback) {
            // Name the active slug in the header: which row is the fallback is
            // configuration, so it should be visible without opening the section.
            const description = fallbackRow
              ? `${section.description ?? ''} Currently ${fallbackType}.`
              : `${section.description ?? ''} No markup row found for "${fallbackType}" — create it before relying on the fallback.`;
            return { ...section, description, rows: fallbackRow ? [fallbackRow] : [] };
          }
          if (section.isDynamic) {
            const prefix = sectionPrefixMap[section.title];
            const dynamicRows = prefix ? dynamicRowsByPrefix[prefix] ?? [] : [];
            // Guard the case where the lookup points at one of the hardcoded base
            // rows — it must appear in the fallback section only, never in both.
            const baseRows = section.rows.filter((r) => r.type !== fallbackType);
            return {
              ...section,
              rows: [...baseRows, ...dynamicRows]
            };
          }
          return section;
        });

        setSections(mergedSections);

        // `accountCount` rides the list response — no extra round trip.
        const nextCounts: Record<string, number> = {};
        data.data.forEach((item: MarkUp) => {
          nextCounts[item.type] = item.accountCount ?? 0;
        });
        setAccountCounts(nextCounts);

        const next = buildInitialServerData(mergedSections);
        const nextMeta: Record<string, { updatedAt: string; updatedByUserName: string | null }> = {};
        data.data.forEach((item: MarkUp) => {
          if (!next[item.type]) return;
          const nextRow: MarkupField = {
            value: item.muvalue,
            ...(next[item.type].price !== undefined ? { price: item.price ?? 0 } : {}),
            title: item.title,
            description: item.description
          };
          if (isModelType(item.type)) {
            for (const key of MODEL_PURPOSE_KEYS) {
              (nextRow as Record<string, unknown>)[key] = (item as unknown as Record<string, unknown>)[key] ?? '';
            }
            for (const key of NUMERIC_PURPOSE_KEYS) {
              const raw = (item as unknown as Record<string, unknown>)[key] as number | undefined;
              (nextRow as Record<string, unknown>)[key] = raw ?? 0;
            }
            for (const key of OPTIONAL_OVERRIDE_KEYS) {
              // Preserve null (no override) rather than defaulting to 0 —
              // the draft-side blank/number distinction depends on it.
              const raw = (item as unknown as Record<string, unknown>)[key] as
                | number
                | null
                | undefined;
              (nextRow as Record<string, unknown>)[key] = raw ?? null;
            }
            for (const key of OPTIONAL_MODEL_OVERRIDE_KEYS) {
              (nextRow as Record<string, unknown>)[key] =
                (item as unknown as Record<string, unknown>)[key] ?? null;
            }
          }
          next[item.type] = nextRow;
          nextMeta[item.type] = {
            updatedAt: item.updatedAt,
            updatedByUserName: item.updatedByUserName ?? null
          };
        });
        setServerData(next);
        setDraft(buildDraftFromServer(next));
        setUpdatedMeta(nextMeta);
      } else {
        setSnack({ open: true, severity: 'error', message: 'Failed to load markup values.' });
      }
    } catch (error) {
      console.error('Error fetching markup values:', error);
      setSnack({ open: true, severity: 'error', message: 'Failed to load markup values.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const saveChanges = useCallback(async () => {
    if (saving) return;

    if (hasValidationErrors) {
      setSnack({ open: true, severity: 'error', message: 'Fix invalid fields before saving.' });
      return;
    }

    type UpdatePayload = { type: string } & Partial<Record<'muvalue' | 'price' | 'title' | 'description' | PurposeFieldKey, number | string | null>>;
    const updates: UpdatePayload[] = [];

    Object.keys(draft).forEach((type) => {
      const nextValue = parseNumber(draft[type].value);
      if (nextValue !== null && nextValue !== serverData[type]?.value) {
        updates.push({ type, muvalue: nextValue });
      }
      if (draft[type].price !== undefined) {
        const nextPrice = parseNumber(draft[type].price ?? '');
        if (nextPrice !== null && nextPrice !== serverData[type]?.price) {
          updates.push({ type, price: nextPrice });
        }
      }
      if (draft[type].title !== undefined) {
        const nextTitle = (draft[type].title ?? '').trim();
        const serverTitle = (serverData[type]?.title ?? '').trim();
        if (nextTitle && nextTitle !== serverTitle) {
          updates.push({ type, title: nextTitle });
        }
      }
      if (draft[type].description !== undefined) {
        const nextDesc = draft[type].description ?? '';
        const serverDesc = serverData[type]?.description ?? '';
        if (nextDesc !== serverDesc) {
          updates.push({ type, description: nextDesc });
        }
      }
      for (const key of MODEL_PURPOSE_KEYS) {
        if (draft[type][key] === undefined) continue;
        const next = draft[type][key] ?? '';
        const prev = (serverData[type]?.[key] as string | undefined) ?? '';
        if (next !== prev) {
          updates.push({ type, [key]: next });
        }
      }
      for (const key of NUMERIC_PURPOSE_KEYS) {
        if (draft[type][key] === undefined) continue;
        const parsed = parseNumber(draft[type][key] ?? '');
        if (parsed === null) continue;
        const prev = (serverData[type]?.[key] as number | undefined) ?? 0;
        if (parsed !== prev) {
          updates.push({ type, [key]: parsed });
        }
      }
      for (const key of OPTIONAL_MODEL_OVERRIDE_KEYS) {
        if (draft[type][key] === undefined) continue;
        const next = (draft[type][key] ?? '').trim();
        const prev = (serverData[type]?.[key] as string | null | undefined) ?? '';
        // Blank clears the override (explicit null), same as the numeric ones.
        if (next !== prev) updates.push({ type, [key]: next || null });
      }
      for (const key of OPTIONAL_OVERRIDE_KEYS) {
        if (draft[type][key] === undefined) continue;
        const raw = draft[type][key] ?? '';
        const prev = (serverData[type]?.[key] as number | null | undefined) ?? null;
        if (raw.trim() === '') {
          // Explicit clear — must send `null`, not be skipped, or a
          // previously-set override can never be removed from the UI.
          if (prev !== null) updates.push({ type, [key]: null });
        } else {
          const parsed = parseNumber(raw);
          if (parsed !== null && parsed !== prev) {
            updates.push({ type, [key]: parsed });
          }
        }
      }
    });

    if (updates.length === 0) {
      setSnack({ open: true, severity: 'success', message: 'No changes to save.' });
      return;
    }

    setSaving(true);
    try {
      for (const payload of updates) {
        await callUpdateMarupValues(payload);
      }

      const nextServer: MarkupFormData = { ...serverData };
      updates.forEach((u) => {
        if (!nextServer[u.type]) return;
        const merged: MarkupField = { ...nextServer[u.type] };
        if (u.muvalue !== undefined) merged.value = u.muvalue as number;
        if (u.price !== undefined) merged.price = u.price as number;
        if (u.title !== undefined) merged.title = u.title as string;
        if (u.description !== undefined) merged.description = u.description as string;
        for (const key of MODEL_PURPOSE_KEYS) {
          if (u[key] !== undefined) (merged as Record<string, unknown>)[key] = u[key];
        }
        for (const key of NUMERIC_PURPOSE_KEYS) {
          if (u[key] !== undefined) (merged as Record<string, unknown>)[key] = u[key];
        }
        for (const key of [...OPTIONAL_OVERRIDE_KEYS, ...OPTIONAL_MODEL_OVERRIDE_KEYS]) {
          if (u[key] !== undefined) (merged as Record<string, unknown>)[key] = u[key];
        }
        nextServer[u.type] = merged;
      });
      setServerData(nextServer);
      setDraft(buildDraftFromServer(nextServer));
      setSnack({ open: true, severity: 'success', message: 'Saved changes.' });
    } catch (error) {
      console.error('Error saving markup values:', error);
      setSnack({
        open: true,
        severity: 'error',
        message: 'Failed to save changes. Please retry.'
      });
    } finally {
      setSaving(false);
    }
  }, [draft, hasValidationErrors, saving, serverData]);

  const loadAiModels = useCallback(async () => {
    try {
      const res = await callGetAiModels();
      setAiModels(res.data?.data ?? []);
    } catch (error) {
      console.error('Error loading AI models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  /**
   * A plan is removable only if it was created through "Add New Markup" — the six
   * `BASE_AI_ROWS` types are seeded rows the screen renders from a hardcoded list,
   * so deleting one leaves a row with no data behind it. The fallback tier and any
   * tier with accounts on it are also off limits. The server enforces all four
   * rules; this just decides whether to offer the button.
   */
  const canDeleteMarkup = useCallback(
    (type: string): boolean =>
      type.startsWith('ai_processing_') &&
      !BASE_DYNAMIC_TYPES.has(type) &&
      type !== fallbackMarkupType &&
      (accountCounts[type] ?? 0) === 0,
    [accountCounts, fallbackMarkupType]
  );

  const handleDeleteMarkup = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const res = await callDeleteMarkup(deleteTarget.type);
      if (res.code === 200) {
        setSnack({ open: true, severity: 'success', message: `"${deleteTarget.label}" deleted.` });
        setDeleteTarget(null);
        await loadMarkupValues();
      } else {
        // The guard messages name which rule rejected it (system tier / fallback /
        // still in use), so surface the server's text rather than a generic error.
        setSnack({
          open: true,
          severity: 'error',
          message: res.error?.message ?? 'Failed to delete plan.'
        });
      }
    } catch (error) {
      console.error('Error deleting markup:', error);
      setSnack({ open: true, severity: 'error', message: 'Failed to delete plan.' });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, loadMarkupValues]);

  const isAiDialog = dialogPrefix === 'ai_processing';

  const handleCreateMarkup = useCallback(async () => {
    if (!newName.trim()) {
      setSnack({ open: true, severity: 'error', message: 'Name is required.' });
      return;
    }

    const type = `${dialogPrefix}_${slugify(newName)}`;

    // Duplicate check
    const allTypes = sections.flatMap((s) => s.rows.map((r) => r.type));
    if (allTypes.includes(type)) {
      setSnack({ open: true, severity: 'error', message: `A markup with type "${type}" already exists.` });
      return;
    }

    // Build payload — different shape depending on whether this is an AI processing plan.
    // For AI rows we prepend the "AI Processing " prefix to the stored title so it matches
    // the existing naming convention (e.g. "AI Processing Response").
    const displayTitle = isAiDialog
      ? `AI Processing ${newName.trim()}`
      : newName.trim();
    const payload: CreateMarkupPayload = {
      type,
      title: displayTitle,
      muvalue: 0,
      ...(newDescription.trim() ? { description: newDescription.trim() } : {})
    };

    if (isAiDialog) {
      // At least one purpose must have a model chosen, otherwise there's nothing to price
      const anyModelSelected = PURPOSES.some((p) => newPurposeDrafts[p].model.trim() !== '');
      if (!anyModelSelected) {
        setSnack({
          open: true,
          severity: 'error',
          message: 'Select a model for at least one purpose (AI / Sentiment / Enquiries).'
        });
        return;
      }

      // Validate numeric entries & fold drafts into the payload
      for (const purpose of PURPOSES) {
        const d = newPurposeDrafts[purpose];
        const keys = PURPOSE_KEYS[purpose];

        if (d.model.trim()) (payload as unknown as Record<string, unknown>)[keys.model] = d.model;

        for (const [rawKey, fieldLabel, payloadKey] of [['muvalue', 'markup', keys.muvalue]] as const) {
          const raw = d[rawKey];
          if (!raw.trim()) continue;
          const parsed = parseNumber(raw);
          if (parsed === null) {
            setSnack({
              open: true,
              severity: 'error',
              message: `Enter a valid ${PURPOSE_LABELS[purpose]} ${fieldLabel}.`
            });
            return;
          }
          (payload as unknown as Record<string, unknown>)[payloadKey] = parsed;
        }
      }

      // SR-H6: optional, response-only overrides. Blank = no override.
      for (const [raw, label, key] of [
        [newResponseOverrides.sales, 'Sales response markup', 'responseMuValueSales'],
        [newResponseOverrides.lettings, 'Lettings response markup', 'responseMuValueLettings']
      ] as const) {
        if (!raw.trim()) continue;
        const parsed = parseNumber(raw);
        if (parsed === null) {
          setSnack({ open: true, severity: 'error', message: `Enter a valid ${label}.` });
          return;
        }
        (payload as unknown as Record<string, unknown>)[key] = parsed;
      }
    } else {
      // Legacy non-AI flow: require single model + markup, optional price
      if (!newModel) {
        setSnack({ open: true, severity: 'error', message: 'Please select an AI model.' });
        return;
      }
      const muvalue = parseNumber(newMuvalue);
      if (muvalue === null) {
        setSnack({ open: true, severity: 'error', message: 'Enter a valid markup value.' });
        return;
      }
      const price = parseNumber(newPrice);
      if (price === null) {
        setSnack({ open: true, severity: 'error', message: 'Enter a valid price.' });
        return;
      }
      payload.muvalue = muvalue;
      payload.price = price;
    }

    setCreating(true);
    try {
      const { code } = await callCreateMarkup(payload);

      if (code === 200 || code === 201) {
        setSnack({ open: true, severity: 'success', message: 'New markup created.' });
        setDialogOpen(false);
        setNewName('');
        setNewDescription('');
        setNewModel('');
        setNewMuvalue('');
        setNewPrice('');
        setNewPurposeDrafts({
          response: emptyPurposeDraft(),
          sentiment: emptyPurposeDraft(),
          enquiries: emptyPurposeDraft()
        });
        setNewResponseOverrides({ sales: '', lettings: '' });
        // Reload to pick up the new entry
        await loadMarkupValues();
      } else {
        setSnack({ open: true, severity: 'error', message: 'Failed to create markup.' });
      }
    } catch (error) {
      console.error('Error creating markup:', error);
      setSnack({ open: true, severity: 'error', message: 'Failed to create markup.' });
    } finally {
      setCreating(false);
    }
  }, [
    newName,
    newModel,
    newMuvalue,
    newPrice,
    newPurposeDrafts,
    newResponseOverrides,
    isAiDialog,
    dialogPrefix,
    sections,
    loadMarkupValues
  ]);

  useEffect(() => {
    loadMarkupValues();
  }, [loadMarkupValues]);

  useEffect(() => {
    loadAiModels();
  }, [loadAiModels]);

  // The price a model bills at, as shown to whoever picks it: under each model
  // dropdown and beside every option in it. Prices belong to the model (AI
  // models & prices panel) — a tier can't override them.
  const formatModelPrice = (model: string) => {
    const entry = priceByModel.get(model);
    return entry ? `In $${entry.inputPrice} · Out $${entry.outputPrice} / 1K` : null;
  };

  // `model` is what the row actually runs; `inheritedFrom` names the row it
  // comes from when this row's own dropdown is blank.
  const renderModelPrice = (model: string | null | undefined, inheritedFrom?: string) => {
    if (!model) return null;
    const price = formatModelPrice(model);
    return (
      <Tooltip
        title={
          price
            ? 'This is the price billed for this model. Edit it on the AI Models page.'
            : `${model} is not on the AI Models page — add it there, or this row bills at its old frozen price.`
        }
      >
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: price ? '#0f766e' : '#b45309',
            pl: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {price ?? 'Not priced'}
          {inheritedFrom ? (
            <span style={{ color: '#94a3b8', fontWeight: 400 }}>
              {' '}
              · {model} via {inheritedFrom}
            </span>
          ) : null}
        </Box>
      </Tooltip>
    );
  };

  // Shared renderer for all model dropdowns — sourced from the `ai_model` lookup,
  // with explicit loading / empty states (no hardcoded fallback list).
  const renderModelMenuItems = () => {
    if (modelsLoading) {
      return (
        <MenuItem disabled>
          <em style={{ color: '#94a3b8' }}>Loading models…</em>
        </MenuItem>
      );
    }
    if (aiModelOptions.length === 0) {
      return (
        <MenuItem disabled>
          <em style={{ color: '#94a3b8' }}>No models — add them under AI models &amp; prices</em>
        </MenuItem>
      );
    }
    return aiModelOptions.map((group) => [
      <MenuItem key={group.group} disabled sx={{ opacity: 0.6, fontWeight: 700 }}>
        {group.group}
      </MenuItem>,
      ...group.models.map((model) => (
        <MenuItem key={model} value={model} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <span>{model}</span>
          <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 600 }}>{formatModelPrice(model) ?? ''}</span>
        </MenuItem>
      ))
    ]);
  };

  return (
    <Box sx={{ px: 5, py: 4 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <TextView $fontSize={12} $fontWeight={400} $fontColor="#445" style={{ opacity: 0.9 }}>
          Edit values, then save once. This prevents accidental partial updates and reduces API calls.
        </TextView>

        <Stack direction="row" spacing={1.25} alignItems="center">
          <Tooltip title={dirtyCount === 0 ? 'No pending changes' : `${dirtyCount} pending change(s)`}>
            <Box>
              <TextView $fontSize={12} $fontWeight={700} $fontColor={dirtyCount ? '#9a4b00' : '#2e5d3b'}>
                {dirtyCount ? `${dirtyCount} unsaved` : 'All changes saved'}
              </TextView>
            </Box>
          </Tooltip>

          <Button
            primary={false}
            variant="outlined"
            onClick={resetDraft}
            disabled={dirtyCount === 0 || loading || saving}
          >
            Reset
          </Button>
          <Button onClick={saveChanges} loading={saving} disabled={dirtyCount === 0 || loading || hasValidationErrors}>
            Save changes
          </Button>
          <Button primary={false} variant="text" onClick={loadMarkupValues} disabled={loading || saving}>
            Reload
          </Button>
        </Stack>
      </Stack>

      <TextView $fontSize={12} $fontWeight={400} $fontColor="#445" style={{ marginBottom: 16 }}>
        Model prices are set on{' '}
        <Link component={RouterLink} to={routes.SA_AiModels}>
          AI Models
        </Link>
        ; SR-token conversion rates on{' '}
        <Link component={RouterLink} to={routes.SA_Lookups}>
          Lookups
        </Link>{' '}
        (config).
      </TextView>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Stack spacing={1.5}>
            {sections.map((section, idx) => {
              const meta = sectionMeta[idx];
              const showDirty = Boolean(meta?.dirty);
              const showErrors = Boolean(meta?.errors);

              return (
                <Accordion
                  key={section.title}
                  defaultExpanded={idx === 0}
                  disableGutters
                  sx={{
                    backgroundColor: '#f3f6f4',
                    borderRadius: 1.5,
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 2,
                      py: 1,
                      '& .MuiAccordionSummary-content': { my: 0 }
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Stack spacing={0.25}>
                        <TextView $fontSize={16} $fontWeight={800}>
                          {section.title}
                        </TextView>
                        {section.description ? (
                          <TextView $fontSize={11} $fontWeight={400} $fontColor="#445" style={{ opacity: 0.9 }}>
                            {section.description}
                          </TextView>
                        ) : null}
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {showErrors ? (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              backgroundColor: '#fbe9e7'
                            }}
                          >
                            <TextView $fontSize={11} $fontWeight={800} $fontColor="#b71c1c">
                              {meta.errors} error{meta.errors === 1 ? '' : 's'}
                            </TextView>
                          </Box>
                        ) : null}
                        {showDirty ? (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              backgroundColor: '#fff3e0'
                            }}
                          >
                            <TextView $fontSize={11} $fontWeight={800} $fontColor="#9a4b00">
                              {meta.dirty} unsaved
                            </TextView>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              backgroundColor: 'rgba(46,93,59,0.10)'
                            }}
                          >
                            <TextView $fontSize={11} $fontWeight={800} $fontColor="#2e5d3b">
                              Up to date
                            </TextView>
                          </Box>
                        )}
                      </Stack>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2, pb: 2 }}>
                    <Stack spacing={0.75}>
                      {(() => {
                        const sectionHasModel = section.rows.some((r) => isModelType(r.type));
                        const sectionHasPrice = section.rows.some((r) => Boolean(r.hasPrice));
                        const HEADER_LABELS: Array<{ key: string; label: string; width?: string | number; flex?: number; minWidth?: number }> = sectionHasModel
                          ? [
                              { key: 'name', label: 'Name', width: 220 },
                              { key: 'purpose', label: 'Purpose', width: 110 },
                              { key: 'model', label: 'Model', flex: 2, minWidth: 220 },
                              { key: 'muvalue', label: 'Markup ×', flex: 1, minWidth: 90 }
                            ]
                          : [
                              { key: 'name', label: 'Name', width: 220 },
                              { key: 'value', label: sectionHasPrice ? 'Markup' : 'Markup / Value', flex: 1, minWidth: 110 },
                              ...(sectionHasPrice ? [{ key: 'price', label: 'Price ($)', flex: 1, minWidth: 110 }] : [])
                            ];
                        return (
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.25}
                            sx={{ px: 1.25, pt: 0.5, pb: 0.25, display: { xs: 'none', md: 'flex' } }}
                          >
                            {HEADER_LABELS.map((col) => (
                              <Box
                                key={col.key}
                                sx={{
                                  flex: col.flex ?? undefined,
                                  width: col.width,
                                  minWidth: col.minWidth
                                }}
                              >
                                <TextView $fontSize={11} $fontWeight={700} $fontColor="#64748b">
                                  {col.label.toUpperCase()}
                                </TextView>
                              </Box>
                            ))}
                          </Stack>
                        );
                      })()}
                      {section.rows.map((row) => {
                        const valueErr = validationErrors[row.type]?.value;
                        const priceErr = validationErrors[row.type]?.price;
                        const showPrice = Boolean(row.hasPrice);
                        const showModel = isModelType(row.type);
                        const accountCount = accountCounts[row.type] ?? 0;
                        const tierInUse = accountCount > 0;

                        if (showModel) {
                          return (
                            <Stack
                              key={row.type}
                              spacing={0.5}
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255,255,255,0.65)'
                              }}
                            >
                              {/* Tier header. The slug, the account count and the
                                  actions used to live inside the 220px label column
                                  and at the foot of a three-row card, where they read
                                  as decoration and were easy to miss entirely. A
                                  tier's account count is what decides whether it can
                                  be deleted, so it belongs on the first line, with
                                  the actions opposite it. */}
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ pb: 0.75, mb: 0.5, borderBottom: '1px solid #eef2f7' }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      px: 0.75,
                                      py: 0.1,
                                      borderRadius: 0.75,
                                      backgroundColor: '#eef2ff',
                                      border: '1px solid #e0e7ff'
                                    }}
                                  >
                                    <TextView
                                      $fontSize={9}
                                      $fontWeight={700}
                                      $fontColor="#4f46e5"
                                      style={{ fontFamily: 'monospace', letterSpacing: 0.4 }}
                                    >
                                      {row.type}
                                    </TextView>
                                  </Box>
                                  <Tooltip
                                    title={
                                      tierInUse
                                        ? 'Billing accounts on this plan. Move them to another plan before it can be deleted.'
                                        : 'No billing account uses this plan.'
                                    }
                                  >
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        px: 0.9,
                                        py: 0.15,
                                        borderRadius: 999,
                                        backgroundColor: tierInUse ? 'rgba(46,93,59,0.10)' : '#f1f5f9'
                                      }}
                                    >
                                      <TextView
                                        $fontSize={10}
                                        $fontWeight={800}
                                        $fontColor={tierInUse ? '#2e5d3b' : '#94a3b8'}
                                      >
                                        {tierInUse
                                          ? `${accountCount} account${accountCount === 1 ? '' : 's'}`
                                          : 'Not in use'}
                                      </TextView>
                                    </Box>
                                  </Tooltip>
                                </Stack>

                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Link
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                      setHistoryType(row.type);
                                      setHistoryRoleFilter('all');
                                    }}
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      textDecoration: 'none',
                                      color: '#475569',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                  >
                                    History
                                  </Link>
                                  {canDeleteMarkup(row.type) ? (
                                    <Link
                                      component="button"
                                      type="button"
                                      onClick={() =>
                                        setDeleteTarget({
                                          type: row.type,
                                          label: draft[row.type]?.title || row.label
                                        })
                                      }
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        textDecoration: 'none',
                                        color: '#b91c1c',
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      Delete plan
                                    </Link>
                                  ) : null}
                                </Stack>
                              </Stack>

                              {PURPOSES.map((purpose, idx) => {
                                const keys = PURPOSE_KEYS[purpose];
                                const muErr = validationErrors[row.type]?.[keys.muvalue];
                                // Tier name + description — rendered on the tier's first row.
                                const nameBlock = (
                                        <Stack spacing={0.5}>
                                          <OutlinedInput
                                            value={draft[row.type]?.title ?? row.label}
                                            onChange={(e) => onDraftChange(row.type, 'title', e.target.value)}
                                            size="small"
                                            fullWidth
                                            inputProps={{
                                              'aria-label': 'Markup name',
                                              style: { fontSize: 13, fontWeight: 600, padding: '6px 8px' }
                                            }}
                                          />
                                          <OutlinedInput
                                            value={draft[row.type]?.description ?? ''}
                                            onChange={(e) => onDraftChange(row.type, 'description', e.target.value)}
                                            size="small"
                                            fullWidth
                                            placeholder="Description (optional)"
                                            multiline
                                            minRows={1}
                                            maxRows={3}
                                            inputProps={{
                                              'aria-label': 'Markup description',
                                              style: { fontSize: 11, padding: '4px 8px' }
                                            }}
                                          />
                                        </Stack>
                                );
                                return (
                                  <React.Fragment key={purpose}>
                                  {purpose === 'response'
                                    ? // SR-H6: Sales / Lettings response rows, shown first — above Other response,
                                      // laid out exactly like the purpose rows. Model and markup are
                                      // optional — blank inherits Other response — and the price
                                      // follows whichever model the row ends up running.
                                      ENQUIRY_OVERRIDE_ROWS.map((o) => {
                                        const inheritedMu = String(draft[row.type]?.responseMuValue ?? '') || '—';
                                        const runsModel = draft[row.type]?.[o.model] || draft[row.type]?.responseModel;
                                        return (
                                          <Stack
                                            key={o.model}
                                            direction={{ xs: 'column', md: 'row' }}
                                            alignItems={{ xs: 'stretch', md: 'flex-start' }}
                                            spacing={1.25}
                                          >
                                            <Box
                                              sx={{
                                                width: { xs: '100%', md: 220 },
                                                minWidth: 0,
                                                display: { xs: o.label === 'Sales' ? 'block' : 'none', md: 'block' }
                                              }}
                                            >
                                              {/* Sales is the tier's first row, so it carries the name. */}
                                              {o.label === 'Sales' ? nameBlock : null}
                                            </Box>
                                            <Box sx={{ width: { xs: '100%', md: 110 } }}>
                                              <TextView
                                                $fontSize={12}
                                                $fontWeight={600}
                                                $fontColor="#475569"
                                                sx={{ whiteSpace: 'nowrap' }}
                                              >
                                                {o.label} response
                                              </TextView>
                                            </Box>
                                            <Stack spacing={0.5} sx={{ flex: { md: 2 }, minWidth: 220 }}>
                                            <FormControl size="small" fullWidth>
                                              <Select
                                                displayEmpty
                                                value={draft[row.type]?.[o.model] ?? ''}
                                                size="small"
                                                inputProps={{ 'aria-label': `${o.label} response model` }}
                                                onChange={(e) => onDraftChange(row.type, o.model, String(e.target.value))}
                                                renderValue={(selected) => {
                                                  const s = String(selected ?? '');
                                                  return s ? s : <span style={{ color: '#94a3b8' }}>Same as Other response</span>;
                                                }}
                                              >
                                                <MenuItem value="">
                                                  <em style={{ color: '#64748b' }}>Same as Other response</em>
                                                </MenuItem>
                                                {renderModelMenuItems()}
                                              </Select>
                                            </FormControl>
                                            {renderModelPrice(
                                              runsModel,
                                              draft[row.type]?.[o.model] ? undefined : 'Other response'
                                            )}
                                            </Stack>
                                            <Box sx={{ flex: 1, minWidth: 90 }}>
                                              <Tooltip
                                                title={`${o.label} response markup. Blank = use Other response (${inheritedMu}).`}
                                              >
                                                <OutlinedInput
                                                  value={draft[row.type]?.[o.muvalue] ?? ''}
                                                  size="small"
                                                  type="number"
                                                  placeholder={inheritedMu}
                                                  onChange={(e) => onDraftChange(row.type, o.muvalue, e.target.value)}
                                                  name={`${row.type}.${o.muvalue}`}
                                                  fullWidth
                                                  error={Boolean(validationErrors[row.type]?.[o.muvalue])}
                                                  inputProps={{ 'aria-label': `${o.label} response markup` }}
                                                />
                                              </Tooltip>
                                            </Box>
                                          </Stack>
                                        );
                                      })
                                    : null}
                                  <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                                    spacing={1.25}
                                  >
                                    <Box sx={{ width: { xs: '100%', md: 220 }, minWidth: 0 }}>
                                      {idx === 0 && purpose !== 'response' ? nameBlock : null}
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', md: 110 } }}>
                                      <TextView
                                        $fontSize={12}
                                        $fontWeight={600}
                                        $fontColor="#475569"
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {PURPOSE_LABELS[purpose]}
                                      </TextView>
                                    </Box>
                                    <Stack spacing={0.5} sx={{ flex: { md: 2 }, minWidth: 220 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        displayEmpty
                                        value={draft[row.type]?.[keys.model] ?? ''}
                                        size="small"
                                        inputProps={{ 'aria-label': `${PURPOSE_LABELS[purpose]} model` }}
                                        onChange={(e) => onDraftChange(row.type, keys.model, String(e.target.value))}
                                        renderValue={(selected) => {
                                          const s = String(selected ?? '');
                                          return s ? s : <span style={{ color: '#94a3b8' }}>—</span>;
                                        }}
                                      >
                                        {renderModelMenuItems()}
                                      </Select>
                                    </FormControl>
                                    {/* A blank sentiment / enquiries model runs the response model. */}
                                    {draft[row.type]?.[keys.model]
                                      ? renderModelPrice(draft[row.type]?.[keys.model])
                                      : purpose === 'response'
                                        ? null
                                        : renderModelPrice(draft[row.type]?.responseModel, 'Other response')}
                                    </Stack>
                                    <Box sx={{ flex: 1, minWidth: 90 }}>
                                      <OutlinedInput
                                        value={draft[row.type]?.[keys.muvalue] ?? ''}
                                        size="small"
                                        type="number"
                                        onChange={(e) => onDraftChange(row.type, keys.muvalue, e.target.value)}
                                        name={`${row.type}.${keys.muvalue}`}
                                        fullWidth
                                        error={Boolean(muErr)}
                                        inputProps={{ 'aria-label': `${PURPOSE_LABELS[purpose]} markup` }}
                                      />
                                    </Box>
                                  </Stack>
                                  </React.Fragment>
                                );
                              })}

                              {row.helper ? (
                                <TextView
                                  $fontSize={11}
                                  $fontWeight={400}
                                  $fontColor="#445"
                                  style={{ opacity: 0.85 }}
                                >
                                  {row.helper}
                                </TextView>
                              ) : null}
                              {/* The "View history" link that used to sit here moved
                                  into the tier header, which is always rendered — this
                                  line only appears once a row has been edited. */}
                              {updatedMeta[row.type]?.updatedAt ? (
                                <TextView $fontSize={10} $fontWeight={400} $fontColor="#94a3b8">
                                  {`Last updated ${new Date(updatedMeta[row.type].updatedAt).toLocaleString()}${
                                    updatedMeta[row.type].updatedByUserName
                                      ? ` · by ${updatedMeta[row.type].updatedByUserName}`
                                      : ''
                                  }`}
                                </TextView>
                              ) : null}
                            </Stack>
                          );
                        }

                        // Non-model row (WABA / Twilio / Negotiator)
                        return (
                          <Stack
                            key={row.type}
                            spacing={0.5}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 1,
                              backgroundColor: 'rgba(255,255,255,0.65)'
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              alignItems={{ xs: 'stretch', md: 'center' }}
                              spacing={1.25}
                            >
                              <Box sx={{ width: { xs: '100%', md: 220 }, minWidth: 0 }}>
                                <Stack spacing={0.5}>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignSelf: 'flex-start',
                                      px: 0.75,
                                      py: 0.1,
                                      borderRadius: 0.75,
                                      backgroundColor: '#eef2ff',
                                      border: '1px solid #e0e7ff'
                                    }}
                                  >
                                    <TextView
                                      $fontSize={9}
                                      $fontWeight={700}
                                      $fontColor="#4f46e5"
                                      style={{ fontFamily: 'monospace', letterSpacing: 0.4 }}
                                    >
                                      {row.type}
                                    </TextView>
                                  </Box>
                                  <OutlinedInput
                                    value={draft[row.type]?.title ?? row.label}
                                    onChange={(e) => onDraftChange(row.type, 'title', e.target.value)}
                                    size="small"
                                    fullWidth
                                    inputProps={{
                                      'aria-label': 'Markup name',
                                      style: { fontSize: 13, fontWeight: 600, padding: '6px 8px' }
                                    }}
                                  />
                                  <OutlinedInput
                                    value={draft[row.type]?.description ?? ''}
                                    onChange={(e) => onDraftChange(row.type, 'description', e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="Description (optional)"
                                    multiline
                                    minRows={1}
                                    maxRows={3}
                                    inputProps={{
                                      'aria-label': 'Markup description',
                                      style: { fontSize: 11, padding: '4px 8px' }
                                    }}
                                  />
                                </Stack>
                              </Box>

                              <Box sx={{ flex: 1, minWidth: 110 }}>
                                <OutlinedInput
                                  value={draft[row.type]?.value ?? ''}
                                  size="small"
                                  type="number"
                                  onChange={(e) => onDraftChange(row.type, 'value', e.target.value)}
                                  name={`${row.type}.value`}
                                  fullWidth
                                  error={Boolean(valueErr)}
                                  inputProps={{ 'aria-label': 'Markup' }}
                                />
                                {valueErr ? (
                                  <TextView $fontSize={10} $fontWeight={400} $fontColor="red">
                                    {valueErr}
                                  </TextView>
                                ) : null}
                              </Box>

                              {showPrice ? (
                                <Box sx={{ flex: 1, minWidth: 110 }}>
                                  <OutlinedInput
                                    value={draft[row.type]?.price ?? ''}
                                    size="small"
                                    type="number"
                                    onChange={(e) => onDraftChange(row.type, 'price', e.target.value)}
                                    name={`${row.type}.price`}
                                    fullWidth
                                    error={Boolean(priceErr)}
                                    inputProps={{ 'aria-label': 'Price' }}
                                  />
                                  {priceErr ? (
                                    <TextView $fontSize={10} $fontWeight={400} $fontColor="red">
                                      {priceErr}
                                    </TextView>
                                  ) : null}
                                </Box>
                              ) : null}
                            </Stack>

                            {row.helper ? (
                              <TextView
                                $fontSize={11}
                                $fontWeight={400}
                                $fontColor="#445"
                                style={{ opacity: 0.85 }}
                              >
                                {row.helper}
                              </TextView>
                            ) : null}
                            {updatedMeta[row.type]?.updatedAt ? (
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <TextView $fontSize={10} $fontWeight={400} $fontColor="#94a3b8">
                                  {`Last updated ${new Date(updatedMeta[row.type].updatedAt).toLocaleString()}${
                                    updatedMeta[row.type].updatedByUserName
                                      ? ` · by ${updatedMeta[row.type].updatedByUserName}`
                                      : ''
                                  }`}
                                </TextView>
                                <Link
                                  component="button"
                                  type="button"
                                  onClick={() => {
                                    setHistoryType(row.type);
                                    setHistoryRoleFilter('all');
                                  }}
                                  sx={{ fontSize: 10, textDecoration: 'underline', color: '#64748b' }}
                                >
                                  View history
                                </Link>
                              </Stack>
                            ) : null}
                          </Stack>
                        );
                      })}

                      {section.isDynamic ? (
                        <Box sx={{ pt: 0.5 }}>
                          <Button
                            primary={false}
                            variant="outlined"
                            onClick={() => {
                              const prefixMap: Record<string, string> = {
                                'AI Processing': 'ai_processing'
                              };
                              setDialogPrefix(prefixMap[section.title] ?? 'ai_processing');
                              setDialogSectionTitle(section.title);
                              setDialogOpen(true);
                            }}
                            startIcon={<AddIcon />}
                          >
                            Add New Markup
                          </Button>
                        </Box>
                      ) : null}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Grid>
      </Grid>

      {/* Delete plan confirmation */}
      <Modal open={Boolean(deleteTarget)} onClose={() => (deleting ? undefined : setDeleteTarget(null))}>
        <Box sx={{ width: 460, maxWidth: '95vw' }}>
          <TextView $fontSize={18} $fontWeight={800} mb={2}>
            Delete this plan?
          </TextView>
          <Stack spacing={1.25}>
            <TextView $fontSize={13} $fontWeight={400}>
              {`"${deleteTarget?.label ?? ''}" (${deleteTarget?.type ?? ''}) will be removed from the AI Processing list.`}
            </TextView>
            <TextView $fontSize={11} $fontWeight={400} $fontColor="#64748b">
              No billing account is on this plan. Usage already billed against it keeps its own
              pricing and stays in reports — only the plan is removed.
            </TextView>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button primary={false} variant="text" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button onClick={handleDeleteMarkup} loading={deleting} disabled={deleting}>
              Delete plan
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Create new markup dialog */}
      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Box sx={{ width: isAiDialog ? 720 : 480, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
          <TextView $fontSize={18} $fontWeight={800} mb={2}>
            Add New {dialogSectionTitle} Markup
          </TextView>
          <Stack spacing={2.5} sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
            <Stack spacing={0.5} sx={{ mt: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <TextView $fontSize={12} $fontWeight={700}>
                  Name
                </TextView>
                <TextView $fontSize={10} $fontWeight={400} $fontColor="#64748b">
                  Type: <strong>{`${dialogPrefix}_${slugify(newName) || '…'}`}</strong>
                </TextView>
              </Stack>
              <OutlinedInput
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                size="small"
                fullWidth
                name="newMarkupName"
                placeholder={isAiDialog ? 'Response' : 'e.g. My Custom Model'}
                startAdornment={
                  isAiDialog ? (
                    <InputAdornment position="start">
                      <span style={{ color: '#475569', fontWeight: 600 }}>AI Processing</span>
                    </InputAdornment>
                  ) : undefined
                }
                inputProps={{ 'aria-label': 'Markup name' }}
              />
            </Stack>

            <Stack spacing={0.5}>
              <TextView $fontSize={12} $fontWeight={700}>
                Description <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
              </TextView>
              <OutlinedInput
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                name="newMarkupDescription"
                placeholder="What this plan is for, internal notes, etc."
                inputProps={{ 'aria-label': 'Markup description' }}
              />
            </Stack>

            {isAiDialog ? (
              <Stack spacing={1.25}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                  <TextView $fontSize={12} $fontWeight={700} $fontColor="#64748b">
                    PER-PURPOSE MODEL &amp; MARKUP
                  </TextView>
                  <TextView $fontSize={10} $fontWeight={400} $fontColor="#64748b">
                    Markup is a multiplier (e.g. <strong>5</strong> → 100 tokens billed as 500). Prices come from the
                    model (AI models &amp; prices). Blanks can be filled in later.
                  </TextView>
                </Stack>

                {PURPOSES.map((purpose) => {
                  const draftP = newPurposeDrafts[purpose];
                  return (
                    <Stack
                      key={purpose}
                      spacing={1}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        border: '1px solid rgba(148,163,184,0.3)',
                        backgroundColor: 'rgba(248,250,252,0.6)'
                      }}
                    >
                      <TextView $fontSize={13} $fontWeight={700} $fontColor="#1f2937">
                        {PURPOSE_LABELS[purpose]}
                      </TextView>

                      <FormControl size="small" fullWidth>
                        <InputLabel id={`new-${purpose}-model-label`}>Model</InputLabel>
                        <Select
                          labelId={`new-${purpose}-model-label`}
                          value={draftP.model}
                          size="small"
                          label="Model"
                          onChange={(e) => updatePurposeDraft(purpose, 'model', String(e.target.value))}
                        >
                          <MenuItem value="">
                            <em style={{ color: '#94a3b8' }}>— none —</em>
                          </MenuItem>
                          {renderModelMenuItems()}
                        </Select>
                      </FormControl>
                      {draftP.model
                        ? renderModelPrice(draftP.model)
                        : purpose === 'response'
                          ? null
                          : renderModelPrice(newPurposeDrafts.response.model, 'Response')}

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                        <TextField
                          value={draftP.muvalue}
                          onChange={(e) => updatePurposeDraft(purpose, 'muvalue', e.target.value)}
                          label="Markup × (multiplier)"
                          labelSize={12}
                          labelWeight={700}
                          size="small"
                          type="number"
                          fullWidth
                          name={`new.${purpose}.muvalue`}
                        />
                      </Stack>

                      {purpose === 'response' ? (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                          <TextField
                            value={newResponseOverrides.sales}
                            onChange={(e) =>
                              setNewResponseOverrides((prev) => ({
                                ...prev,
                                sales: e.target.value
                              }))
                            }
                            label="Sales response markup (optional)"
                            labelSize={12}
                            labelWeight={700}
                            size="small"
                            type="number"
                            fullWidth
                            placeholder="— (uses Other response markup)"
                            name="new.response.responseMuValueSales"
                          />
                          <TextField
                            value={newResponseOverrides.lettings}
                            onChange={(e) =>
                              setNewResponseOverrides((prev) => ({
                                ...prev,
                                lettings: e.target.value
                              }))
                            }
                            label="Lettings response markup (optional)"
                            labelSize={12}
                            labelWeight={700}
                            size="small"
                            type="number"
                            fullWidth
                            placeholder="— (uses Other response markup)"
                            name="new.response.responseMuValueLettings"
                          />
                        </Stack>
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel id="model-select-label">AI Model</InputLabel>
                  <Select
                    labelId="model-select-label"
                    value={newModel}
                    label="AI Model"
                    onChange={(e) => setNewModel(e.target.value)}
                  >
                    {renderModelMenuItems()}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1.5}>
                  <TextField
                    value={newMuvalue}
                    onChange={(e) => setNewMuvalue(e.target.value)}
                    label="Markup Value"
                    labelSize={12}
                    labelWeight={700}
                    size="small"
                    type="number"
                    fullWidth
                    name="newMarkupMuvalue"
                  />

                  <TextField
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    label="Price ($)"
                    labelSize={12}
                    labelWeight={700}
                    size="small"
                    type="number"
                    fullWidth
                    name="newMarkupPrice"
                  />
                </Stack>
              </>
            )}

            <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button primary={false} variant="text" onClick={() => setDialogOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button onClick={handleCreateMarkup} loading={creating} disabled={creating}>
                Create
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Box>
          <Alert
            message={snack.message}
            severity={snack.severity}
            variant="filled"
            onClose={closeSnack}
          />
        </Box>
      </Snackbar>

      <Dialog open={!!historyType} onClose={() => setHistoryType(undefined)} maxWidth="md" fullWidth>
        <DialogTitle>
          Markup History
          {historyType && (
            <Typography variant="body2" color="text.secondary">
              {historyType}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Role</Typography>
            <MuiSelect
              size="small"
              value={historyRoleFilter}
              onChange={(e) => setHistoryRoleFilter(e.target.value as 'all' | MarkupHistoryRole)}
              sx={{ minWidth: 160 }}
            >
              <MuiMenuItem value="all">All</MuiMenuItem>
              <MuiMenuItem value="base">Base</MuiMenuItem>
              <MuiMenuItem value="response">Response</MuiMenuItem>
              <MuiMenuItem value="sentiment">Sentiment</MuiMenuItem>
              <MuiMenuItem value="enquiries">Enquiries</MuiMenuItem>
              <MuiMenuItem value="meta">Meta (title/description)</MuiMenuItem>
            </MuiSelect>
          </Stack>
          {historyQuery.isLoading && (
            <Stack direction="row" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {historyQuery.error && (
            <Alert message="Could not load history." severity="error" />
          )}
          {!historyQuery.isLoading && !historyQuery.error && (
            <MuiTable size="small">
              <TableHead>
                <MuiTableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>By</TableCell>
                </MuiTableRow>
              </TableHead>
              <TableBody>
                {(historyQuery.data ?? [])
                  .filter((row) => historyRoleFilter === 'all' || row.role === historyRoleFilter)
                  .map((row) => (
                    <MuiTableRow key={row.id}>
                      <TableCell>{new Date(row.changedAt).toLocaleString()}</TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell>{row.field}</TableCell>
                      <TableCell>{row.oldValue ?? '— (initial)'}</TableCell>
                      <TableCell>{row.newValue || '(empty)'}</TableCell>
                      <TableCell>{row.changedByUserName ?? '—'}</TableCell>
                    </MuiTableRow>
                  ))}
                {(historyQuery.data ?? []).filter((row) => historyRoleFilter === 'all' || row.role === historyRoleFilter).length === 0 && (
                  <MuiTableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" align="center" py={2}>
                        No changes recorded for this filter.
                      </Typography>
                    </TableCell>
                  </MuiTableRow>
                )}
              </TableBody>
            </MuiTable>
          )}
        </DialogContent>
        <DialogActions>
          <Button primary={false} variant="text" onClick={() => setHistoryType(undefined)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminMarkupMargins;
